/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, inject, signal} from '@angular/core';
import * as _ from 'lodash';
import template from './team-edit-page.component.html';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {ADN_TARGETING_TYPES} from '../../targeting/targeting-constants';
import {TargetingEditorModalService} from '../../targeting/targeting-editor-modal.service';
import {AngularModalService} from '../../common/modal/angular-modal.service';
import {getAvailableTeamTypes, initFeesForParam, preTeamSaveFix} from './team-commons';
import {TeamObjectMembershipModalComponent} from './team-object-membership-modal.component';
import {TeamOwnershipModalComponent} from './team-ownership-modal.component';
import {FolderApiService} from '../../folder/folder-api.service';
import {SiteApiService} from '../../site/site-api.service';
import {TeamGroupApiService} from '../team-group/team-group-api.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {deferSignalStateWrite} from '../../common/util/template-state';
import {TeamApiService, TeamModel} from './team-api.service';
import {ADVERTISING_SHARED} from '../../advertising/advertising-shared';
import {TeamEditFeesComponent} from './team-edit-fees.component';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED, TeamEditFeesComponent],
  selector: 'adn-team-edit-page',
  template
})
export class TeamEditPageComponent extends SignalAdminResourceEditPageBase<TeamModel> {
  private readonly teamApi = inject(TeamApiService);

  applications: string[] = [];
  politicalApp = false;
  canSeeTechFees = false;
  canSwitchAgentBooking = false;
  canSeeTeamGroups = false;
  canSeeSiteLinks = false;
  showLabels = false;
  teamTypes: string[] = [];
  networkCurrencies: string[] = [];
  targetTypes = _.filter(ADN_TARGETING_TYPES, (tt: any) => tt.apiType !== 'THIRD_PARTY_AUDIENCE');
  private readonly allTeamGroupsState = signal<any[] | null>(null);
  private readonly sitesState = signal<any[] | null>(null);
  private readonly matchedSitesState = signal<any[]>([]);
  private readonly unownedSitesState = signal<any[]>([]);
  private readonly ownerSitesState = signal<any[]>([]);
  private readonly foldersState = signal<any[] | null>(null);
  private readonly matchedFoldersState = signal<any[]>([]);
  private readonly targetingResultState = signal<any>({data: false});

  get allTeamGroups(): any[] | null { return this.allTeamGroupsState(); }
  set allTeamGroups(value: any[] | null) { this.allTeamGroupsState.set(value); }
  get sites(): any[] | null { return this.sitesState(); }
  set sites(value: any[] | null) { this.sitesState.set(value); }
  get matchedSites(): any[] { return this.matchedSitesState(); }
  set matchedSites(value: any[]) { this.matchedSitesState.set(value); }
  get unownedSites(): any[] { return this.unownedSitesState(); }
  set unownedSites(value: any[]) { this.unownedSitesState.set(value); }
  get ownerSites(): any[] { return this.ownerSitesState(); }
  set ownerSites(value: any[]) { this.ownerSitesState.set(value); }
  get folders(): any[] | null { return this.foldersState(); }
  set folders(value: any[] | null) { this.foldersState.set(value); }
  get matchedFolders(): any[] { return this.matchedFoldersState(); }
  set matchedFolders(value: any[]) { this.matchedFoldersState.set(value); }
  get targetingResult(): any { return this.targetingResultState(); }
  set targetingResult(value: any) { this.targetingResultState.set(value); }
  siteGroups: any[] | null = null;
  ownerSiteGroups: any[] = [];

  private originalTeamGroupId: string | undefined;

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly siteApi: SiteApiService,
    private readonly folderApi: FolderApiService,
    private readonly teamGroupApi: TeamGroupApiService,
    private readonly angularModalService: AngularModalService,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(inject(TeamApiService), 'team');
    this.applications = _.filter(this.LocalUserPermissions.getApps(), (a: string) => a !== 'SELF_SERVICE');
    this.politicalApp = !!this.LocalNetworkProfile.get('politicalApp');
    if (this.politicalApp && this.applications.indexOf('POLITICAL') < 0) {
      this.applications.push('POLITICAL');
    }
    this.canSeeTechFees = this.LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');
    // The server takes the setting only from a network admin.
    this.canSwitchAgentBooking = this.LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');
    this.canSeeTeamGroups = this.LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      && this.localNetworkInfo.getInfo('teamGroupsCount') > 0;
    this.canSeeSiteLinks = this.LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      || this.LocalUserPermissions.hasNetwork('MANAGE_PUBLISHING')
      || this.LocalUserPermissions.hasAny('AX_PUBLISHER_TEAM_ADMIN')
      || this.LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN');
    this.showLabels = this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.teamTypes = getAvailableTeamTypes(this.LocalNetworkProfile);
    const defaultCurrency = this.LocalNetworkProfile.get('defaultCurrency');
    this.networkCurrencies = _.orderBy(this.LocalNetworkProfile.get('availableCurrencies'), (curr: string) => {
      return curr === defaultCurrency ? 0 : 1;
    });
  }

  get showTypeField(): boolean {
    return this.applications.indexOf('ADVERTISING') > -1 || this.politicalApp;
  }

  countryChangeHook = (): void => {
    this.markExternalFormDirty();
  };

  protected override initialiseModel(model: TeamModel): TeamModel {
    const initialised = super.initialiseModel(model);
    initialised.sites = _.map(initialised.sites || [], 'id');
    initialised.folders = _.map(initialised.folders || [], 'id');
    initialised.defaultTargeting = initialised.defaultTargeting || {};
    return initialised;
  }

  protected override afterEditingModelReset(): void {
    this.afterSaveAfterInit();
    if (!this.isNew) {
      this.siteRefresh();
      this.folderRefresh();
    }
  }

  protected override prepareForSubmit(model: TeamModel): void {
    const currentTeamGroupId = _.get(model, ['teamGroup', 'id']);
    if (this.canSeeTeamGroups && (currentTeamGroupId || this.originalTeamGroupId) && (currentTeamGroupId !== this.originalTeamGroupId)) {
      this.teamGroupApi.updateTeamToTeamGroup(
        currentTeamGroupId || this.originalTeamGroupId,
        model.id,
        !currentTeamGroupId
      ).subscribe();
    }
    if (this.isNew) {
      this.localNetworkInfo.increment('teamsCount');
    }
  }

  protected override prepareSubmitPayload(payload: TeamModel): void {
    // preTeamSaveFix converts ui* fee structures to API fields and may null or drop fee
    // objects; keep that off the live form, which still binds them.
    preTeamSaveFix({model: payload});
  }

  addSupplementaryFee(): void {
    this.markExternalFormDirty();
    this.editingModel.supplementaryFees = this.editingModel.supplementaryFees || [];
    const supFee = {
      type: 'PERCENTAGE',
      name: 'Fee',
      amount: null
    };
    initFeesForParam(supFee, this.networkCurrencies);
    this.editingModel.supplementaryFees.unshift(supFee);
  }

  deleteFee(index: number): void {
    this.markExternalFormDirty();
    _.pullAt(this.editingModel.supplementaryFees, index);
    if (this.editingModel.supplementaryFees.length === 0) {
      this.addSupplementaryFee();
    }
  }

  removeLayoutFee(index: number): void {
    _.pullAt(this.editingModel.uiLayoutFees, index);
    if (this.editingModel.uiLayoutFees.length === 0) {
      this.addLayoutFee();
    }
    this.markExternalFormDirty();
  }

  addLayoutFee(): void {
    this.editingModel.uiLayoutFees = this.editingModel.uiLayoutFees || [];
    const defaultFee = {fee: {type: 'MULTI_CURRENCY_MONETARY'}};
    initFeesForParam(defaultFee.fee, this.networkCurrencies);
    this.editingModel.uiLayoutFees.push(defaultFee);
    this.markExternalFormDirty();
  }

  removeTargetingFee(index: number): void {
    _.pullAt(this.editingModel.uiTargetingTypeTechFees, index);
    if (this.editingModel.uiTargetingTypeTechFees.length === 0) {
      this.addTargetingFee();
    }
    this.markExternalFormDirty();
  }

  addTargetingFee(): void {
    this.editingModel.uiTargetingTypeTechFees = this.editingModel.uiTargetingTypeTechFees || [];
    const defaultFee = {fee: {type: 'MULTI_CURRENCY_MONETARY'}};
    initFeesForParam(defaultFee.fee, this.networkCurrencies);
    this.editingModel.uiTargetingTypeTechFees.push(defaultFee);
    this.markExternalFormDirty();
  }

  editTarget(targeting: any): void {
    this.targetingResult = {data: false};
    this.targetingEditorModalService.open({
      lineItemType: 'NORMAL',
      modelLineItem: _.cloneDeep(this.editingModel.data || {}),
      pageType: 'marketplaceProduct',
      reachEnabled: undefined,
      mandatoryTargets: undefined,
      availableTargets: null,
      overridableTargeting: undefined,
      targetingMode: null,
      targeting: targeting,
      isLockedDown: false
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      _.assign(targeting, wc.wc);
      // New object reference so adn-target-summary ngOnChanges rebuilds the summary.
      this.editingModel.defaultTargeting = _.cloneDeep(targeting);
      if (!this.isNew) {
        this.teamApi.saveDefaultTargeting(this.editingModel.id, this.editingModel.defaultTargeting).subscribe((respLineItem: any) => {
          this.targetingResult = {...this.targetingResult, data: true};
          this.editingModel = {...this.editingModel, validationWarnings: respLineItem.validationWarnings};
          // Saved directly: keep page state in step so Undo / later saves don't revert it.
          this.state.update({
            ...this.model,
            defaultTargeting: _.cloneDeep(this.editingModel.defaultTargeting),
            validationWarnings: respLineItem.validationWarnings
          });
        });
      } else {
        this.markExternalFormDirty();
      }
    });
  }

  editSiteOwnership(removeMode = false): void {
    const modalRef = this.angularModalService.show(TeamOwnershipModalComponent, {
      class: 'modal-lg',
      initialState: {
        obj: this.editingModel,
        removeMode,
        ownerSites: this.ownerSites,
        unownedSites: this.unownedSites
      }
    });

    (modalRef.content as TeamOwnershipModalComponent).onSaved.subscribe(() => {
      this.sites = null;
      this.siteRefresh();
      setTimeout(() => {
        this.editingModel.updateValidationWarnings?.();
      }, 500);
    });
  }

  editSiteMembership(): void {
    const modalRef = this.angularModalService.show(TeamObjectMembershipModalComponent, {
      class: 'modal-lg',
      initialState: {
        theResource: this.siteApi,
        translation: 'Site',
        obj: this.editingModel,
        objects: this.sites,
        matchedObjects: this.matchedSites
      }
    });

    (modalRef.content as TeamObjectMembershipModalComponent).onSaved.subscribe(() => {
      this.sites = null;
      this.siteRefresh();
      setTimeout(() => {
        this.editingModel.updateValidationWarnings?.();
      }, 500);
    });
  }

  editFolderMembership(): void {
    const modalRef = this.angularModalService.show(TeamObjectMembershipModalComponent, {
      class: 'modal-lg',
      initialState: {
        theResource: this.folderApi,
        translation: 'Folder',
        obj: this.editingModel,
        objects: this.folders,
        matchedObjects: this.matchedFolders
      }
    });

    (modalRef.content as TeamObjectMembershipModalComponent).onSaved.subscribe(() => {
      this.folders = null;
      this.folderRefresh();
      setTimeout(() => {
        this.editingModel.updateValidationWarnings?.();
      }, 500);
    });
  }

  private afterSaveAfterInit(): void {
    if (!this.editingModel.type) {
      if (this.LocalUserPermissions.hasNetworkPermission('AX_ADVERTISER_ADMIN')) {
        this.editingModel.type = 'AX_ADVERTISER';
      } else if (this.teamTypes && this.teamTypes.length) {
        this.editingModel.type = this.teamTypes.indexOf('STANDARD') > -1 ? 'STANDARD' : this.teamTypes[0];
      } else {
        this.editingModel.type = 'STANDARD';
      }
    }
    this.editingModel.techFeeCpm = this.editingModel.techFeeCpm || {type: 'PERCENTAGE'};
    this.editingModel.creativeTagTechFeeCpm = this.editingModel.creativeTagTechFeeCpm || {type: 'MULTI_CURRENCY_MONETARY'};
    initFeesForParam(this.editingModel.techFeeCpm, this.networkCurrencies);
    initFeesForParam(this.editingModel.creativeTagTechFeeCpm, this.networkCurrencies);
    this.teamGroupQuery();
    this.initialiseLayoutFees();
    this.initTargetTypeFees();
    this.editingModel.supplementaryFees = _.get(this.editingModel, 'supplementaryFees', []);
    _.forEach(this.editingModel.supplementaryFees, (fee: any) => {
      initFeesForParam(fee, this.networkCurrencies);
    });
  }

  private teamGroupQuery(): void {
    const teamGroupsCount = this.localNetworkInfo.getInfo('teamGroupsCount');
    if (teamGroupsCount > 0 && this.canSeeTeamGroups && !this.allTeamGroups && !this.isNew) {
      this.teamGroupApi.listOptions({filterBy: 'type', filterByValue: this.editingModel.type}).subscribe((page) => {
        // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
        deferSignalStateWrite(() => {
          this.allTeamGroups = page.results;
          this.editingModel.teamGroup = _.find(this.allTeamGroups, (tg: any) => {
            return _.find(tg.teams, (t: any) => this.editingModel.id === t.id);
          }) || null;
          this.originalTeamGroupId = _.get(this.editingModel, ['teamGroup', 'id']);
        });
      });
    }
  }

  private initialiseLayoutFees(): void {
    const layoutFees = _.get(this.editingModel, 'layoutFees');
    this.editingModel.uiLayoutFees = _.isArray(layoutFees) ? layoutFees : [];
    this.editingModel.uiLayoutFees[0] = this.editingModel.uiLayoutFees[0] ? this.editingModel.uiLayoutFees[0] : {};
    this.editingModel.uiLayoutFees = _.map(this.editingModel.uiLayoutFees, (layoutFee: any) => {
      const adjFee = _.cloneDeep(layoutFee) || {};
      adjFee.fee = adjFee.fee || {type: 'MULTI_CURRENCY_MONETARY'};
      initFeesForParam(adjFee.fee, this.networkCurrencies);
      return adjFee;
    });
  }

  private initTargetTypeFees(): void {
    this.editingModel.uiTargetingTypeTechFees = _.get(this.editingModel, 'targetingTypeTechFees', []);
    this.editingModel.uiTargetingTypeTechFees = _.map(this.editingModel.uiTargetingTypeTechFees, (valFee: any, key: string) => {
      return {apiType: key, fee: valFee};
    });
    if (_.isEmpty(this.editingModel.uiTargetingTypeTechFees)) {
      this.editingModel.uiTargetingTypeTechFees = [];
    }
    if (!this.editingModel.uiTargetingTypeTechFees.length) {
      this.editingModel.uiTargetingTypeTechFees.push({fee: {type: 'MULTI_CURRENCY_MONETARY'}});
    }
    _.forEach(this.editingModel.uiTargetingTypeTechFees, (fee: any) => {
      initFeesForParam(fee.fee, this.networkCurrencies);
    });
  }

  private siteRefresh(): void {
    if (this.applications.indexOf('ADVERTISING') < 0 || !this.editingModel?.id) {
      return;
    }
    this.siteApi.listOptions({pageSize: 1000, team: this.editingModel.id}).subscribe((page) => {
      this.matchedSites = _.map(page.results, (sg: any) => {
        const indexedTeams = _.keyBy(sg.teams, 'id');
        return _.assign({}, _.pick(sg, ['name', 'description', 'id', 'teams', 'ownerTeam']), {
          indexedTeams: indexedTeams,
          isOwned: !!_.get(sg.ownerTeam, ['id']),
          isOwner: _.get(sg.ownerTeam, ['id']) === this.editingModel.id
        });
      });
      this.unownedSites = _.filter(this.matchedSites, {isOwned: false});
      this.ownerSites = _.filter(this.matchedSites, {isOwner: true});
    });
    this.siteApi.listOptions({pageSize: 1000}).subscribe((page) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        this.sites = page.results;
      });
    });
  }

  private folderRefresh(): void {
    if (this.applications.indexOf('DATA') < 0 || !this.editingModel?.id) {
      return;
    }
    this.folderApi.listOptions({pageSize: 1000}).subscribe((page) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        this.folders = _.map(page.results, (sg: any) => {
          const indexedTeams = _.keyBy(sg.teams, 'id');
          return _.assign({}, _.pick(sg, ['name', 'description', 'id', 'teams']), {
            indexedTeams: indexedTeams,
            isMatched: !!indexedTeams[this.editingModel.id]
          });
        });
        this.matchedFolders = _.filter(this.folders, {isMatched: true});
      });
    });
  }
}
